import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRIORITIES } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { copyRoot } from "@/lib/clipboard";
import NewCategoryDialog from "@/components/NewCategoryDialog";
import { Sprout, Copy, Check, RefreshCw, Loader2, Plus } from "lucide-react";

export default function QuickGenerate({ onCreated }) {
  const { settings } = useSettings();
  const { activeId } = useWorkspace();
  const categories = settings?.categories || [];
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("none");
  const [caret, setCaret] = useState(true);
  const [tailType, setTailType] = useState("date");
  const [tailValue, setTailValue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [code, setCode] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [override, setOverride] = useState("");

  const baseRef = useRef("");
  const reqRef = useRef(0);
  const timerRef = useRef(null);

  const runPreview = async (avoid = null) => {
    const reqId = ++reqRef.current;
    setPreviewing(true);
    try {
      const res = await api.post("/tasks/generate-from-description", {
        description,
        symbol: caret ? "^" : "",
        tail_type: tailType,
        tail_value: tailValue,
        avoid,
      });
      if (reqId !== reqRef.current) return;
      setCode(res.data.code);
      baseRef.current = res.data.base || "";
    } catch (e) {
      if (reqId === reqRef.current) setCode("");
    } finally {
      if (reqId === reqRef.current) setPreviewing(false);
    }
  };

  // Live preview as you finish typing (debounced)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (description.trim().length < 3) {
      reqRef.current++;
      setCode("");
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    timerRef.current = setTimeout(() => runPreview(null), 650);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, caret, tailType, tailValue]);

  const remix = () => { if (code) runPreview(baseRef.current); };

  const copyCode = async () => {
    if (!code) return;
    await copyRoot(code);
    setCopied(true);
    toast.success("Root copied", { description: code });
    setTimeout(() => setCopied(false), 1400);
  };

  const setRoot = async () => {
    if (!description.trim()) return toast.error("Describe your task first");
    if (overrideMode && !override.trim()) return toast.error("Paste a root, or switch to auto-generate");
    if (overrideMode && override.trim().length > (settings?.max_code_len ?? 24)) return toast.error(`Root must be ${settings?.max_code_len ?? 24} characters or fewer`);
    setSaving(true);
    try {
      let finalCode;
      if (overrideMode) {
        finalCode = override.trim();
      } else {
        finalCode = code;
        if (!finalCode) {
          const gen = await api.post("/tasks/generate-from-description", {
            description, symbol: caret ? "^" : "", tail_type: tailType, tail_value: tailValue,
          });
          finalCode = gen.data.code;
        }
      }
      await api.post("/tasks", { title: description, code: finalCode, priority, notes: "", category: category === "none" ? "" : category, workspace_id: activeId || null });
      toast.success("Root planted 🥕", { description: finalCode });
      setDescription(""); setTailValue(""); setCode(""); baseRef.current = ""; setOverride(""); setCategory("none");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set root");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="quick-generate" data-testid="quick-generate" className="rounded-xl border border-border bg-card p-2.5 mb-3 shadow-sm">
      <div className="flex flex-col gap-1.5">
        {/* Row A: describe + priority + set root */}
        <div className="flex flex-wrap items-center gap-2">
          <Sprout className="w-4 h-4 text-primary shrink-0" />
          <Input
            data-testid="qg-title-input"
            placeholder="Describe your task, e.g. Carrot Project Kickoff"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !saving) setRoot(); }}
            className="h-8 flex-1 min-w-[200px]"
          />
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="qg-priority-select" className="h-8 w-[112px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {categories.length > 0 && (
            <Select value={category} onValueChange={(v) => { if (v === "__new__") { setNewCatOpen(true); } else { setCategory(v); } }}>
              <SelectTrigger data-testid="qg-category-select" className="h-8 w-[130px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No category</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                <SelectItem value="__new__" data-testid="qg-category-new" className="text-primary font-semibold">+ New category…</SelectItem>
              </SelectContent>
            </Select>
          )}
          {categories.length === 0 && (
            <Button type="button" variant="outline" size="sm" data-testid="qg-add-category-btn" onClick={() => setNewCatOpen(true)} className="h-8 rounded-md gap-1 text-muted-foreground">
              <Plus className="w-3.5 h-3.5" /> Category
            </Button>
          )}
          <Button onClick={setRoot} disabled={saving} data-testid="qg-add-btn" className={`h-8 bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5 ${overrideMode ? "hidden" : ""}`}>
            <Sprout className="w-4 h-4" /> {saving ? "Planting…" : "Plant your root"}
          </Button>
        </div>

        {/* Row B: options + live preview, or paste-your-own */}
        <div className="flex flex-wrap items-center gap-2 sm:pl-6 text-xs text-muted-foreground">
          {overrideMode ? (
            <>
              <span className="font-semibold text-foreground">Your root</span>
              <Input
                data-testid="qg-override-input"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !saving) setRoot(); }}
                placeholder="Paste an existing root, e.g. ^CarrotProject"
                maxLength={settings?.max_code_len ?? 24}
                className="h-8 font-mono flex-1 min-w-[180px]"
              />
              <Button type="button" onClick={setRoot} disabled={saving} data-testid="qg-override-add-btn" className="h-8 bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5">
                <Sprout className="w-4 h-4" /> {saving ? "Planting…" : "Plant"}
              </Button>
              <Button type="button" variant="ghost" size="sm" data-testid="qg-auto-mode-btn" onClick={() => setOverrideMode(false)} className="h-8 rounded-full text-primary hover:bg-primary/10">
                Auto-generate instead
              </Button>
            </>
          ) : (
            <>
              <label className="flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-input bg-background cursor-pointer select-none shrink-0">
                <Checkbox data-testid="qg-caret-checkbox" checked={caret} onCheckedChange={(v) => setCaret(!!v)} />
                <span>Include caret <span className="font-mono font-bold text-foreground">^</span></span>
              </label>
              <span>ending in</span>
              <Select value={tailType} onValueChange={setTailType}>
                <SelectTrigger data-testid="qg-tail-type-select" className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">today's date</SelectItem>
                  <SelectItem value="date_yesterday">yesterday's date</SelectItem>
                  <SelectItem value="date_tomorrow">tomorrow's date</SelectItem>
                  <SelectItem value="numerical">a number</SelectItem>
                  <SelectItem value="other">custom</SelectItem>
                </SelectContent>
              </Select>
              {tailType !== "date" && tailType !== "date_yesterday" && tailType !== "date_tomorrow" && (
                <Input
                  data-testid="qg-tail-value-input"
                  placeholder={tailType === "numerical" ? "auto" : "tail"}
                  value={tailValue}
                  onChange={(e) => setTailValue(e.target.value)}
                  className="h-8 font-mono w-[84px]"
                />
              )}

              {/* live preview chip */}
              <div className="ml-auto flex items-center gap-1.5" data-testid="qg-preview">
                {previewing ? (
                  <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> shortening…
                  </span>
                ) : code ? (
                  <>
                    <button onClick={copyCode} data-testid="qg-copy-code" title="Copy root" className="inline-flex items-center gap-2 h-8 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300 font-mono font-bold px-3 text-sm hover:bg-amber-500/20 active:scale-95 transition-all">
                      {code}
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                    <Button type="button" onClick={remix} size="icon" variant="ghost" data-testid="qg-remix-btn" title="Remix — try a different root" className="h-8 w-8 rounded-full text-primary hover:bg-primary/10">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <span className="text-muted-foreground/70">root appears as you type</span>
                )}
              </div>
              <Button type="button" variant="ghost" size="sm" data-testid="qg-override-btn" onClick={() => setOverrideMode(true)} className="h-8 rounded-full text-primary hover:bg-primary/10">
                Use my own
              </Button>
            </>
          )}
        </div>
      </div>
      <NewCategoryDialog open={newCatOpen} onClose={() => setNewCatOpen(false)} onCreated={(cat) => setCategory(cat.id)} />
    </div>
  );
}
